import ReconnectingWebSocket from "reconnecting-websocket";
import { tokenService } from "./tokenService";
import { API_CONFIG } from "./apiConfig";

// Use API_CONFIG.websocketURL instead of hardcoded URL to support different environments
const WEBSOCKET_URL = API_CONFIG.websocketURL;

class WebSocketService {
  constructor() {
    this.rws = null;
    this.listeners = new Map();
    this.isConnected = false;
    this.isRefreshingToken = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttemptsBeforeTokenRefresh = 2;
    this.heartbeatInterval = null;
    this.lastMessageTime = null;

    tokenService.onTokenChange(() => {
      if (this.isRefreshingToken) return;
      if (!this.rws) return;

      const recentlyReceivedMessage =
        this.lastMessageTime && Date.now() - this.lastMessageTime < 5000;

      if (recentlyReceivedMessage) {
        console.log("[WebSocket] Active message flow detected, deferring reconnect (URL factory will use fresh token on next connect)");
        return;
      }

      console.log("[WebSocket] Token changed externally, reconnecting with new token...");
      this.reconnect();
    });
  }

  /**
   * Detecta si un mensaje del servidor indica token expirado
   */
  isTokenExpiredMessage(data) {
    if (!data || typeof data !== 'object') return false;
    
    // Verificar si el mensaje indica token expirado
    const message = data.message || data.error || data.reason || '';
    const messageStr = typeof message === 'string' ? message.toLowerCase() : '';
    
    return (
      messageStr.includes('token') && 
      (messageStr.includes('expired') || 
       messageStr.includes('invalid') || 
       messageStr.includes('unauthorized'))
    ) || data.code === 401 || data.code === 403;
  }

  /**
   * Envía mensajes de heartbeat para mantener la conexión activa
   * Evita que el servidor cierre la conexión por inactividad
   */
  startHeartbeat() {
    // Limpiar cualquier heartbeat anterior
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

  
    this.heartbeatInterval = setInterval(() => {
      if (this.isReady()) {
        try {
         
          const heartbeatMessage = JSON.stringify({
            type: "ping" 
          });
          this.rws.send(heartbeatMessage);
          console.log("[WebSocket] Heartbeat sent");
        } catch (error) {
          console.error("[WebSocket] Error sending heartbeat", error);
        }
      } else {
        // Si no está conectado, limpiar el intervalo
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    }, 0.5 * 60 * 1000); // 5 minutos
  }

  /**
   * Refresca el token y reconecta
   */
  async handleTokenRefresh() {
    if (this.isRefreshingToken) {
      console.log("[WebSocket] Token refresh already in progress, waiting...");
      return;
    }

    this.isRefreshingToken = true;
    console.log("[WebSocket] Token expired, refreshing...");

    try {
      const tenantUuid = localStorage.getItem("workspace_uuid");

      // Refresh token: tries backend refresh_token first, Auth0 fallback second
      const newToken = await tokenService.refreshTokenAuth0(tenantUuid || undefined);
      console.log("[WebSocket] Token refreshed successfully");

      // Limpiar heartbeat
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }

      // Cerrar la conexión actual
      if (this.rws) {
        this.rws.removeEventListener("open", this.handleOpen);
        this.rws.removeEventListener("message", this.handleMessage);
        this.rws.removeEventListener("error", this.handleError);
        this.rws.removeEventListener("close", this.handleClose);
        this.rws.close();
        this.rws = null;
      }

      this.isConnected = false;
      this.isRefreshingToken = false;
      this.reconnectAttempts = 0; // Reset counter

      // Reconectar con el nuevo token
      console.log("[WebSocket] Reconnecting with new token...");
      this.connect();

    } catch (error) {
      console.error("[WebSocket] Failed to refresh token", error);
      this.isRefreshingToken = false;
      this.emit("tokenRefreshError", error);
      // Opcional: desconectar completamente si no se puede refrescar
      // this.disconnect();
    }
  }

  // Handlers como métodos de clase para poder removerlos correctamente
  handleOpen = (event) => {
    console.log("[WebSocket] Connected successfully", event);
    this.isConnected = true;
    this.reconnectAttempts = 0; // Reset counter on successful connection

    const token = tokenService.getToken();
    if (!token) {
      console.warn("[WebSocket] No token available after connection");
      return;
    }

    try {
      const authMessage = JSON.stringify({
        type: "authorization",
        Authorization: token,
      });
      this.rws.send(authMessage);
      console.log("[WebSocket] Authorization message sent as first message");
    } catch (err) {
      console.warn("[WebSocket] Could not send auth message:", err);
    }

    // Iniciar heartbeat para mantener la conexión activa
    this.startHeartbeat();

    this.emit("open", event);
  };

  handleMessage = (event) => {
    this.lastMessageTime = Date.now();
    console.log("[WebSocket] Message received", event.data);
    try {
      const data = JSON.parse(event.data);
      
      // Verificar si el mensaje indica token expirado
      if (this.isTokenExpiredMessage(data)) {
        console.warn("[WebSocket] Server indicates token expired");
        this.handleTokenRefresh();
        return;
      }
      
      this.emit("message", data);
    } catch (error) {
      console.error("[WebSocket] Error parsing message", error);
      this.emit("message", event.data);
    }
  };

  handleError = (error) => {
    const errorDetails = {
      type: error.type,
      readyState: error.target?.readyState,
      url: error.target?.url,
    };
    
    console.error("[WebSocket] Error occurred", {
      error,
      details: errorDetails,
      readyStateText: this.getState(),
    });
    
    if (error.target?.readyState === WebSocket.CLOSED) {
      console.error("[WebSocket] Connection closed immediately. Possible causes:");
      console.error("  - Invalid or expired token");
      console.error("  - Server rejecting authentication");
      console.error("  - Wrong URL or endpoint");
      console.error("  - Token format issue (check Postman configuration)");
    }
    
    this.emit("error", error);
  };

  handleClose = (event) => {
    const closeCodeMeanings = {
      1000: "Normal closure",
      1001: "Going away",
      1002: "Protocol error",
      1003: "Unsupported data",
      1006: "Abnormal closure (no close frame)",
      1008: "Policy violation (likely auth issue)",
      1011: "Internal error",
    };
    
    console.log("[WebSocket] Connection closed", {
      code: event.code,
      reason: event.reason || "No reason provided",
      wasClean: event.wasClean,
      codeMeaning: closeCodeMeanings[event.code] || `Unknown code: ${event.code}`,
    });
    
    // Limpiar heartbeat cuando se cierra la conexión
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    // Detectar si el cierre es por token expirado
    if (event.code === 1008) {
      console.error("[WebSocket] Policy violation - likely authentication issue");
      
      // Verificar si el reason indica token expirado
      const reason = (event.reason || "").toLowerCase();
      if (reason.includes("token") || reason.includes("expired") || reason.includes("unauthorized")) {
        console.log("[WebSocket] Token expired detected, refreshing...");
        this.handleTokenRefresh();
        return; // No emitir 'close' todavía, esperar a reconectar
      }
      
      // Si no es por token expirado pero es 1008, intentar refrescar de todas formas
      // (puede ser token inválido)
      if (!this.isRefreshingToken) {
        console.log("[WebSocket] Attempting token refresh due to policy violation...");
        this.handleTokenRefresh();
        return;
      }
    }
    
    // Si es 1006 (abnormal closure) después de varios intentos, puede ser token expirado
    if (event.code === 1006) {
      this.reconnectAttempts++;
      console.log(`[WebSocket] Abnormal closure detected. Reconnect attempts: ${this.reconnectAttempts}`);
      
      // Si falló varias veces, probablemente el token expiró
      if (this.reconnectAttempts >= this.maxReconnectAttemptsBeforeTokenRefresh && !this.isRefreshingToken) {
        console.log("[WebSocket] Multiple failed reconnection attempts, refreshing token...");
        this.reconnectAttempts = 0; // Reset counter
        this.handleTokenRefresh();
        return;
      }
    } else {
      // Reset counter si el cierre fue por otra razón
      this.reconnectAttempts = 0;
    }
    
    this.isConnected = false;
    this.emit("close", event);
  };

  connect() {
    // Si ya estamos refrescando el token, esperar
    if (this.isRefreshingToken) {
      console.log("[WebSocket] Waiting for token refresh to complete...");
      return;
    }

    if (this.rws && this.rws.readyState === WebSocket.OPEN) {
      console.log("[WebSocket] Already connected");
      return;
    }

    const token = tokenService.getToken();
    if (!token) {
      console.warn("[WebSocket] No token available, cannot connect");
      return;
    }

    console.log("[WebSocket] Attempting to connect...", { 
      url: WEBSOCKET_URL,
      tokenLength: token.length,
      tokenPreview: token.substring(0, 20) + "..."
    });

    try {
      const urlProvider = () => {
        const currentToken = tokenService.getToken();
        return `${WEBSOCKET_URL}?Authorization=${encodeURIComponent(currentToken)}`;
      };

      console.log("[WebSocket] Connecting with token as query parameter (URL provider)");
      
      this.rws = new ReconnectingWebSocket(urlProvider, [], {
        maxRetries: 10,
        maxReconnectionDelay: 10000,
        minReconnectionDelay: 1000,
        reconnectionDelayGrowFactor: 1.3,
        connectionTimeout: 4000,
        debug: false,
      });

      // Usar los handlers como métodos de clase para poder removerlos
      this.rws.addEventListener("open", this.handleOpen);
      this.rws.addEventListener("message", this.handleMessage);
      this.rws.addEventListener("error", this.handleError);
      this.rws.addEventListener("close", this.handleClose);

    } catch (error) {
      console.error("[WebSocket] Failed to create connection", error);
      this.emit("error", error);
    }
  }

  disconnect() {
    console.log("[WebSocket] Disconnecting...");
    
    // Limpiar heartbeat
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.rws) {
      // Remover listeners antes de cerrar
      this.rws.removeEventListener("open", this.handleOpen);
      this.rws.removeEventListener("message", this.handleMessage);
      this.rws.removeEventListener("error", this.handleError);
      this.rws.removeEventListener("close", this.handleClose);
      
      this.rws.close();
      this.rws = null;
    }
    this.isConnected = false;
    this.isRefreshingToken = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Disconnects and reconnects the WebSocket (useful when token changes due to workspace switch)
   * This method assumes the token has already been updated in localStorage
   */
  reconnect() {
    if (this.isRefreshingToken) {
      return;
    }
    
    this.disconnect();
    this.connect();
  }

  send(data) {
    if (!this.rws || this.rws.readyState !== WebSocket.OPEN) {
      console.warn("[WebSocket] Cannot send message, connection not open. State:", this.getState());
      return false;
    }

    try {
      const message = typeof data === "string" ? data : JSON.stringify(data);
      this.rws.send(message);
      console.log("[WebSocket] Message sent", message);
      return true;
    } catch (error) {
      console.error("[WebSocket] Error sending message", error);
      return false;
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    console.log(`[WebSocket] Listener added for event: ${event}`);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;

    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
      console.log(`[WebSocket] Listener removed for event: ${event}`);
    }
  }

  emit(event, data) {
    if (!this.listeners.has(event)) return;

    const callbacks = this.listeners.get(event);
    callbacks.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[WebSocket] Error in event listener for ${event}`, error);
      }
    });
  }

  getState() {
    if (!this.rws) return "CLOSED";
    switch (this.rws.readyState) {
      case WebSocket.CONNECTING:
        return "CONNECTING";
      case WebSocket.OPEN:
        return "OPEN";
      case WebSocket.CLOSING:
        return "CLOSING";
      case WebSocket.CLOSED:
        return "CLOSED";
      default:
        return "UNKNOWN";
    }
  }

  isReady() {
    return this.isConnected && this.rws && this.rws.readyState === WebSocket.OPEN;
  }
}

export const websocketService = new WebSocketService();
export default websocketService;

